/** Domain meeting & ketersediaan. Semua rute di sini menerima JWT. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../shared/api/client'

export const kunci = {
  jadwalSaya: ['meeting', 'saya'],
  tanggalMeeting: ['meeting', 'tanggal'],
  ketersediaan: ['meeting', 'ketersediaan'],
  tenant: ['meeting', 'tenant'],
}

/** Detail meeting hari ini dan mendatang yang melibatkan akun yang login. */
export function useJadwalSaya() {
  return useQuery({
    queryKey: kunci.jadwalSaya,
    queryFn: () => api.get('/dashboard/my-meetings'),
  })
}

/**
 * `month` WAJIB — rutenya menuntutnya, dan memanggilnya tanpa itu menjawab 422.
 *
 * Itu yang terjadi sampai 10 September: layar Hari Ini memanggilnya tanpa bulan, 422-nya
 * berupa array objek, dan renderer galat menampilkannya sebagai `[object Object]`. Dua
 * cacat menumpuk — yang satu membuat permintaannya gagal, yang lain membuat sebabnya
 * tidak terbaca.
 *
 * Bawaannya bulan berjalan di zona PENGGUNA. Memakai UTC akan meleset satu bulan di
 * tanggal 1 pagi WIB — kalender kosong tanpa satu pun galat.
 */
function bulanIni() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export function useTanggalMeeting(month) {
  const bulan = month || bulanIni()
  return useQuery({
    queryKey: [...kunci.tanggalMeeting, bulan],
    queryFn: () => api.get(`/dashboard/meeting-dates?month=${bulan}`),
  })
}

/**
 * Semua meeting tenant yang mendatang, dan yang masih menunggu jawaban prospek (layar
 * Meeting rancang ulang). Tiap meeting membawa pesertanya dan `izin_bentrok`: peserta yang
 * izin di tanggal itu (ADR-096). `useJadwalSaya` hanya memuat meeting orangnya sendiri.
 */
export function useMeetingTenant() {
  return useQuery({
    queryKey: kunci.tenant,
    queryFn: () => api.get('/dashboard/meetings'),
  })
}

/**
 * `month` WAJIB — rutenya menuntutnya, dan tanpa itu balasannya 422.
 *
 * Hook ini memanggilnya tanpa `month` sejak dibuat, jadi tab "Ketersediaan saya" selalu
 * gagal. Gejalanya tidak terlihat sebagai galat: balasan rute ini berbentuk OBJEK
 * `{ blocks, meeting_dates }`, sementara layarnya memperlakukannya sebagai larik dan
 * memeriksa `.length` — yang pada objek bernilai undefined. Jadi cabang "kosong" dan
 * cabang "ada isi" dua-duanya tidak terpenuhi, dan panelnya merender NOL baris.
 * Salah dua kali, dan keduanya diam.
 */
export function useKetersediaan(month) {
  const bulan = month || bulanIni()
  return useQuery({
    queryKey: [...kunci.ketersediaan, bulan],
    queryFn: () => api.get(`/dashboard/availability?month=${bulan}`),
  })
}

// Izin seseorang mengubah tiga hal sekaligus: kalendernya, siapa yang bisa dipilih jadi
// peserta, dan meeting mana yang pesertanya bentrok.
function segarkanIzin(qc) {
  qc.invalidateQueries({ queryKey: kunci.ketersediaan })
  qc.invalidateQueries({ queryKey: kunci.tenant })
  qc.invalidateQueries({ queryKey: ['tim'] })
  // Izin seluruh tim menutup tanggal di "Yang ditawarkan bot sekarang" (P2, ADR-124).
  qc.invalidateQueries({ queryKey: ['ta', 'meeting-pratinjau'] })
}

export function useBlokirTanggal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (isi) => api.post('/dashboard/availability', isi),
    onSuccess: () => segarkanIzin(qc),
  })
}

/**
 * Hapus izin pemanggilnya di satu TANGGAL. Backend membatasi login manusia ke izinnya
 * sendiri, jadi cukup tanggalnya: kartu "Tanggal saya izin" tidak perlu membawa id baris.
 */
export function useHapusIzin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (tanggal) => api.del(`/dashboard/availability/by-date/${tanggal}`),
    onSuccess: () => segarkanIzin(qc),
  })
}

export function useBukaBlokir() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (blockId) => api.del(`/dashboard/availability/${blockId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: kunci.ketersediaan }),
  })
}
